<template>
  <highcharts :options="areaChartOption" />
</template>
  
<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { ColorType, TooltipFormatterContextObject } from 'highcharts'
import { computed } from 'vue'

import type { LivewatchScheduledSupplyCategoryInfo } from '~/api/service/livewatch/schedule-supply-tracker.service'
import { setCommaWithDigits } from '~/utils/numberFormat'
  
  interface Props {
    modelValue: number
    series?: Array<LivewatchScheduledSupplyCategoryInfo>
    colorMap?: Record<string, ColorType>
  }
  
const props = defineProps<Props>()
const selectedDate = useVModel(props, 'modelValue')
const { createCustomTooltip } = useChartTooltip()
  
// plotLine 레이블의 정렬과 오프셋을 계산하는 computed
const plotLineConfig = computed(() => {
  if (!props.series?.length) return { align: 'center', x: 0 }
    
  // 모든 시리즈의 데이터에서 최소/최대 날짜 구하기
  const allDates: number[] = []
  props.series.forEach(serie => {
    serie.data.forEach(([ timestamp ]) => {
      allDates.push(timestamp)
    })
  })
    
  if (allDates.length === 0) return { align: 'center', x: 0 }
    
  const minDate = Math.min(...allDates)
  const maxDate = Math.max(...allDates)
  const dateRange = maxDate - minDate
    
  if (dateRange === 0) return { align: 'center', x: 0 }
    
  // 현재 선택된 날짜의 위치 비율 계산 (0~1)
  const position = (selectedDate.value - minDate) / dateRange
    
  // 양쪽 끝에서는 안쪽으로 정렬하고 오프셋 조정
  if (position <= 0.05) {
    return { align: 'left', x: -15 } // 왼쪽에서 15px 오프셋
  }
  if (position >= 0.95) {
    return { align: 'right', x: 15 } // 오른쪽에서 -15px 오프셋
  }
  return { align: 'center', x: 0 }
})
  
const areaChartOption = computed(() => {
  return {
    title: undefined, 
    chart: {
      type: 'area',
      backgroundColor: 'transparent',
      width: null,
      marginLeft: 60, // 왼쪽 여백 증가
      zooming: {
        mouseWheel: {
          enabled: false
        }
      },
      events: {
        click: function (this, evt: any) {
          const x = evt.xAxis[0].value
          const date = new Date(x)
          date.setHours(23, 59, 59, 999)
          selectedDate.value = date.getTime() + 1
        }
      },
      // spacingBottom: 20,
    },
    annotations: [
      {
        draggable: '',
      },
    ],
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    navigator: {
      enabled: true,
      margin: 10,
      height: 30,
      maskFill: 'rgba(var(--bc-dark), var(--o005))',
      outlineColor: 'rgba(var(--bc-gray), var(--o02))',
      outlineWidth: 1,
      handles: {
        backgroundColor: 'rgba(var(--bc-white), var(--o08))',
        borderColor: 'rgba(var(--bc-dark), var(--o02))',
        width: 14,
        height: 24,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: '\'Inter\', \'Noto Sans KR\', sans-serif',
          },
          // formatter: function (this) {
          //   return xAxisFormatter(this, chartOptions.dateFormatLang, ref(0))
          // },
        },
      },
    },
      
    rangeSelector: {
      enabled: false,
    },
    zooming: {
      mouseWheel: false,
    },
    xAxis: {
      type: 'datetime',
      labels: {
        style: {
          color: 'white',
          fontSize: '12px',
          fontFamily: 'Montserrat',
        },
        format: '{value:%Y/%m}',
      },
      plotLines: [
        {
          dashStyle: 'ShortDash',
          width: 1,
          zIndex: 201,
          value: selectedDate.value,
          label: {
            textAlign: plotLineConfig.value.align,
            x: plotLineConfig.value.x,
            rotation: 0,
            formatter: function (this: any) {
              return `<div style="background-color: #f5f6f9; mb-1; padding: 4px 10px; border-radius: 10px; font-size: 12px; font-weight: 700;">${new Date(selectedDate.value).toISOString().split('T')[0]}</div>`
            },
            useHTML: true,
          },
        },
      ],
    },
    yAxis: [
      {
        title: {
          text: null,
        },
        tickAmount: 7,
        opposite: true,
        softMin: 0,
        labels: {
          style: {
            color: 'white', 
            fontSize: '12px',
            fontFamily: 'Montserrat',
          },
          align: 'left',
          formatter: function (this: any) {
            return `${this.axis.defaultLabelFormatter.call(this)}`
          }
        },
        startOnTick: false,
        showFirstLabel: true,
        showLastLabel: false,
        gridLineColor: 'rgba(var(--bc-gray), var(--o02))',
      },
    ],
    plotOptions: {
      area: {
        stacking: 'stacked',
        fillOpacity: 0.2
      }, 
      series: {
        showInNavigator: true,
        borderWidth: 0,
        marker: {
          enabled: false,
        },
        dataGrouping: {
          enabled: false,
        },
        events: {
          click: function (this: any, evt: any) {
            try {
              const date = new Date(evt.point.category)
              date.setHours(0, 0, 0, 0)
              selectedDate.value = date.getTime()
            } catch (e) {
              console.error(e)
            }
          },
        },
      },
    },
    series: props.series?.map((s: any, idx: number) => ({
      ...s,
      color: props.colorMap?.[s.name] ?? s.color,
    })),
    legend: {
      enabled: false,
    },
    tooltip: {
      split: false,
      shared: true,
      shadow: false,
      outside: true,
      useHTML: true,
      padding: 8,
      backgroundColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      style: {
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      formatter: function (this: TooltipFormatterContextObject) {
        return createCustomTooltip(this, {
          containerClass: 'w-[450px] max-w-[90dvw]',
          date: (timestamp) => `<p class="font-bold">${new Date(timestamp).toISOString().slice(0, 10)}</p>`,
          content: (points: any[]) => {
            return points.map(point => 
              `<div class="flex w-full items-center justify-between">
                  <p><span style="color:${point.series.userOptions.color || point.color};">● </span>${point.series.name}</p>
                  <div class="font-bold">${setCommaWithDigits(point.y, 2)} NXPC</div>
                </div>`
            ).join('')
          },
          footer: (points) => {
            const total = points.reduce((acc, point) => acc + point.y, 0)
            return `<div class="flex w-full items-center justify-between">
                  <p>Total</p>
                  <div class="font-bold">${setCommaWithDigits(total, 2)} NXPC</div>
              </div>`
          },
        })
      }
    },
  }
})
</script>

<style scoped lang="scss">
  .chart-wrapper {
    width: 100%;
    position: relative;
    .selected-date {
      width: max-content;
      padding: 4px 8px;
      box-sizing: border-box;
      border-radius: 24px;
      background-color: rgba(var(--bc-dark), var(--o005));
      backdrop-filter: blur(32px);
  
      color: rgba(var(--bc-dark), var(--o1));
      font-size: 12px;
      font-weight: 700;
  
      position: absolute;
      top: 0;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
    }
  
    .chart {
      width: 100%;
    }
  }
  :deep(.highcharts-plot-line-label) {
    top: 0px !important;
  }
</style>  